import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Truck, Package, MapPin, Clock, Shield, Users, ArrowRight, Mail, Phone, CheckCircle, Menu, X } from 'lucide-react';
import TermsAndPrivacy from './pages/TermsAndPrivacy';
import Contact from './pages/Contact';

// Navigation Component
function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleNavClick = (sectionId) => {
    setIsMenuOpen(false);
    if (sectionId === 'policy') {
      navigate('/terms-and-privacy-policy');
    } else if (sectionId === 'contact') {
      navigate('/contact');
    } else {
      if (location.pathname !== '/') {
        navigate('/');
      }
      // Scroll to section after navigation
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-zinc-950/95 backdrop-blur-md shadow-lg shadow-amber-500/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <div>
              <h1 className="text-xl font-bold font-mono tracking-tight">DMI LOGISTICS</h1>
              <p className="text-[10px] text-amber-400 font-mono tracking-wider">LLC</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8 p-4">
            <button onClick={() => handleNavClick('services')} className="nav-link text-sm font-medium text-zinc-100 hover:text-amber-400 transition-colors">Services</button>
            <button onClick={() => handleNavClick('about')} className="nav-link text-sm font-medium text-zinc-100 hover:text-amber-400 transition-colors">About</button>
            <button onClick={() => handleNavClick('contact')} className="nav-link text-sm font-medium text-zinc-100 hover:text-amber-400 transition-colors">Contact</button>

            <button onClick={() => handleNavClick('policy')} className="nav-link text-sm font-medium text-zinc-100 hover:text-amber-400 transition-colors">
              Terms & Privacy
            </button>

            <button onClick={() => handleNavClick('contact')} className="px-6 py-2.5 bg-amber-500 text-zinc-950 font-semibold rounded-lg hover:bg-amber-400 transition-all hover:scale-105">
              Get Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-amber-400"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4 animate-slide-in bg-zinc-950 rounded-lg p-4 -mx-2">
            <button onClick={() => handleNavClick('services')} className="block w-full text-left text-sm font-medium hover:text-amber-400 transition-colors">Services</button>
            <button onClick={() => handleNavClick('about')} className="block w-full text-left text-sm font-medium hover:text-amber-400 transition-colors">About</button>
            <button onClick={() => handleNavClick('contact')} className="block w-full text-left text-sm font-medium hover:text-amber-400 transition-colors">Contact</button>
            <button onClick={() => handleNavClick('policy')} className="block w-full text-left text-sm font-medium hover:text-amber-400 transition-colors">Terms & Privacy</button>
            <button onClick={() => handleNavClick('contact')} className="block w-full px-6 py-2.5 bg-amber-500 text-zinc-950 font-semibold rounded-lg hover:bg-amber-400 transition-all text-center">
              Get Quote
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

// Home Page Component
function HomePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    smsConsent: false
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNavClick = (sectionId) => {
    if (sectionId === 'policy') {
      navigate('/terms-and-privacy-policy');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    // Telegram Bot Configuration
    const BOT_TOKEN = '8516976444:AAFt2MfAZU48VwXZs9m3tZ_Hm7AqsCjYIss';
    const CHAT_IDS = ['-5144353387', '8284010454']; // Group chat and user chat

    // Format message for Telegram
    const telegramMessage = `
🚚 *New Contact Form Submission*

👤 *Name:* ${formData.name}
📧 *Email:* ${formData.email}
📱 *Phone:* ${formData.phone}
💬 *Message:* ${formData.message}
📲 *SMS Consent:* ${formData.smsConsent ? 'Yes ✅' : 'No ❌'}

---
Submitted at: ${new Date().toLocaleString()}
    `.trim();

    try {
      // Send message to both chats
      const sendPromises = CHAT_IDS.map(chatId =>
        fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chat_id: chatId,
            text: telegramMessage,
            parse_mode: 'Markdown'
          })
        })
      );

      const responses = await Promise.all(sendPromises);
      const results = await Promise.all(responses.map(r => r.json()));

      // Check if all messages were sent successfully
      const allSuccess = results.every(data => data.ok);

      if (allSuccess) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          smsConsent: false
        });
      } else {
        throw new Error('Failed to send message to one or more chats');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Oops! Something went wrong. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Freight Transportation",
      description: "Reliable ground transportation services across the United States with real-time tracking and on-time delivery guarantees."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Warehousing Solutions",
      description: "Secure storage facilities with advanced inventory management systems and flexible space options."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Express Delivery",
      description: "Time-critical shipping solutions with priority handling and expedited delivery options."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Cargo Insurance",
      description: "Comprehensive coverage options to protect your shipments throughout the entire logistics chain."
    }
  ];

  const stats = [
    { number: "15K+", label: "Deliveries" },
    { number: "500+", label: "Clients" },
    { number: "99.8%", label: "On-Time Rate" },
    { number: "24/7", label: "Support" }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grid-pattern pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-950/50 to-zinc-950"></div>

        {/* Animated Background Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center z-10">
          <div className="animate-slide-in">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <p className="text-amber-400 text-sm font-mono">Logistics Solutions That Move Business Forward</p>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
              <span className="gradient-text">Delivering</span>
              <br />
              <span className="text-zinc-100">Excellence</span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-400 mb-8 max-w-3xl mx-auto font-light">
              Professional logistics and freight services across the United States. Your cargo, our commitment.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a href="#contact" className="px-8 py-4 bg-amber-500 text-zinc-950 font-bold rounded-lg hover:bg-amber-400 transition-all hover:scale-105 flex items-center space-x-2 shadow-lg shadow-amber-500/30">
                <span>Request Quote</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#services" className="px-8 py-4 border-2 border-amber-500/30 text-amber-400 font-semibold rounded-lg hover:bg-amber-500/10 transition-all flex items-center space-x-2">
                <span>Our Services</span>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-16">
              {stats.map((stat, index) => (
                <div key={index} className="text-center" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">{stat.number}</div>
                  <div className="text-sm text-zinc-500 font-mono">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative py-24 bg-zinc-900 diagonal-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="gradient-text">Services</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Comprehensive logistics solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="card-hover bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 backdrop-blur-sm"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4 text-amber-400">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-zinc-950 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Why Choose <span className="gradient-text">DMI Logistics</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                With years of experience in the logistics industry, DMI Logistics LLC has built a reputation for reliability, efficiency, and customer satisfaction. We understand that your cargo is more than just freight—it's your business.
              </p>

              <div className="space-y-4">
                {[
                  "Real-time shipment tracking",
                  "Dedicated customer support team",
                  "Competitive pricing with no hidden fees",
                  "Nationwide coverage and partnerships"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-3xl p-8 border border-amber-500/20 backdrop-blur-sm">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Customer First</h3>
                      <p className="text-zinc-400">Your success is our priority</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Secure & Safe</h3>
                      <p className="text-zinc-400">Comprehensive cargo protection</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Always On Time</h3>
                      <p className="text-zinc-400">99.8% on-time delivery rate</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-zinc-900 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Get In <span className="gradient-text">Touch</span>
            </h2>
            <p className="text-zinc-400 text-lg">
              Ready to move your business forward? Contact us today
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Address</h3>
                    <p className="text-zinc-400">425 N. Findlay Street Suite 7</p>
                    <p className="text-zinc-400">Dayton, OH 45404</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Phone</h3>
                    <a href="tel:+16205900000" className="text-amber-400 hover:text-amber-300 transition-colors">
                      +1 (620) 590-0000
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Email</h3>
                    <a href="mailto:dmilogisticsllcsafety@gmail.com" className="text-amber-400 hover:text-amber-300 transition-colors break-all">
                      dmilogisticsllcsafety@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-zinc-300">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-zinc-300">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-zinc-300">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-zinc-300">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100 resize-none"
                    placeholder="Tell us about your logistics needs..."
                  ></textarea>
                </div>

                {/* SMS Opt-in */}
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <input
                      id="sms-consent"
                      name="smsConsent"
                      type="checkbox"
                      checked={formData.smsConsent}
                      onChange={handleInputChange}
                      className="w-4 h-4 border border-zinc-600 rounded bg-zinc-900 focus:ring-3 focus:ring-amber-500/50 text-amber-500 cursor-pointer"
                    />
                  </div>
                  <label htmlFor="sms-consent" className="text-xs text-zinc-400 leading-relaxed">
                    By checking this box, I agree to receive Text messages from DMI LOGISTICS LLC at the phone number provided above. The SMS frequency may vary. Data rates may apply. Text HELP to (620) 590-0000 for assistance. Reply STOP to opt out of messages from DMI LOGISTICS LLC. You will receive no further SMS communication. <button type="button" onClick={() => handleNavClick('policy')} className="text-amber-500 hover:text-amber-400 underline">Terms and Privacy Policy Conditions</button>
                  </label>
                </div>

                {/* Status Message */}
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg ${submitStatus.type === 'success'
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                    : 'bg-red-500/10 border border-red-500/30 text-red-400'
                    }`}>
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-8 py-4 bg-amber-500 text-zinc-950 font-bold rounded-lg transition-all shadow-lg shadow-amber-500/30 ${isSubmitting
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-amber-400 hover:scale-105'
                    }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center">
              <span className="font-bold font-mono">DMI LOGISTICS LLC</span>
            </div>

            <p className="text-zinc-500 text-sm">
              All rights reserved.
            </p>

            <div className="flex space-x-6">
              <button onClick={() => handleNavClick('policy')} className="text-zinc-500 hover:text-amber-400 transition-colors text-sm">
                Terms & Privacy Policy
              </button>
              <a href="#contact" className="text-zinc-500 hover:text-amber-400 transition-colors text-sm">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

// Main App Component
export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans overflow-x-hidden">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Outfit:wght@300;400;500;600;700&display=swap');
          
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html { scroll-behavior: smooth; }
          body { font-family: 'Outfit', sans-serif; overflow-x: hidden; }
          .font-mono { font-family: 'Space Mono', monospace; }
          .animate-float { animation: float 6s ease-in-out infinite; }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          .animate-slide-in { animation: slideIn 0.8s ease-out forwards; }
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .grid-pattern {
            background-image: 
              linear-gradient(rgba(251, 191, 36, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(251, 191, 36, 0.03) 1px, transparent 1px);
            background-size: 50px 50px;
          }
          .gradient-text {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .card-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
          .card-hover:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(251, 191, 36, 0.2);
          }
          .nav-link { position: relative; cursor: pointer; }
          .nav-link::after {
            content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 2px;
            background: #fbbf24; transition: width 0.3s ease;
          }
          .nav-link:hover::after { width: 100%; }
          .diagonal-section { clip-path: polygon(0 5%, 100% 0, 100% 95%, 0 100%); }
          @media (max-width: 768px) { .diagonal-section { clip-path: none; } }
        `}</style>

        <Navigation />

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/terms-and-privacy-policy" element={<TermsAndPrivacy />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </div>
    </Router>
  );
}